/**
 * Sprint 25.6 — pure helpers for the agent page (navigability + graceful
 * liveness) and the attach-page bug fix.
 *
 * Extracted as React-free pure functions so they carry the unit-test coverage
 * in this repo's `tsx scripts/test-*.ts` convention (there is no Vitest /
 * Playwright here — see scripts/test-agent-page-25.6.ts). The components below
 * are thin wrappers over these.
 */

import type { BrainCurrent, Task, TaskReference, TaskWorkflow, RecentEvent } from "../../lib/types";

// ---------------------------------------------------------------------------
// Collection tabs (Phase D) — the tab set + active-tab resolution. Kept here
// (not in AgentTabStrip.tsx) so that component file exports only a component
// (react-refresh/only-export-components).
// ---------------------------------------------------------------------------

export const AGENT_TABS = ["overview", "routines", "pages", "memories", "files", "activity", "settings"] as const;
export type AgentTab = (typeof AGENT_TABS)[number];

/** Resolve the active tab from a trailing route segment (unknown → overview). */
export function resolveAgentTab(segment: string | undefined | null): AgentTab {
  const s = (segment ?? "").toLowerCase();
  return (AGENT_TABS as readonly string[]).includes(s) && s !== "overview" ? (s as AgentTab) : "overview";
}

// ---------------------------------------------------------------------------
// Add-reference (Phase A bug fix) — optimistic list merge.
// ---------------------------------------------------------------------------

/**
 * Insert (or refresh) a reference in the task's references list, newest-first,
 * with no duplicates (keyed on target_kind + target_id). Backs the optimistic
 * update so the row appears the instant the supervisor picks a page — before
 * the server round-trip resolves.
 */
export function applyOptimisticAttach(list: TaskReference[], ref: TaskReference): TaskReference[] {
  const without = list.filter((r) => !(r.target_kind === ref.target_kind && r.target_id === ref.target_id));
  return [ref, ...without];
}

// ---------------------------------------------------------------------------
// Up next — expandable queue (Phase B).
// ---------------------------------------------------------------------------

export const UP_NEXT_COLLAPSED_LIMIT = 5;

/** Whether the "Show all / Show less" toggle should render (queue exceeds the cap). */
export function shouldShowQueueToggle(total: number, limit = UP_NEXT_COLLAPSED_LIMIT): boolean {
  return total > limit;
}

/**
 * The rows to render given the collapsed top-N and (when expanded + available)
 * the full ordered queue. Collapsed → the snapshot's top-N slice; expanded →
 * the full list if it has loaded, otherwise fall back to the slice so the list
 * never goes blank mid-fetch.
 */
export function visibleQueue(
  collapsed: { id: string }[],
  full: { id: string }[] | undefined,
  expanded: boolean,
  limit = UP_NEXT_COLLAPSED_LIMIT,
): { id: string }[] {
  if (!expanded) return collapsed.slice(0, limit);
  if (full && full.length >= collapsed.length) return full;
  return collapsed;
}

/**
 * Reorder a list so the items named in `orderedIds` lead (in that order),
 * followed by every other item in its original order. Backs the optimistic
 * drag-reorder of the queue: the visible rows (always the front of the queue)
 * are renumbered to `orderedIds`, and any rows not currently shown keep their
 * relative order behind them — mirroring the `sort_order = index` writes.
 */
export function reorderById<T extends { id: string }>(list: T[], orderedIds: string[]): T[] {
  const byId = new Map(list.map((x) => [x.id, x] as const));
  const lead = orderedIds.map((id) => byId.get(id)).filter((x): x is T => !!x);
  const leadSet = new Set(orderedIds);
  const rest = list.filter((x) => !leadSet.has(x.id));
  return [...lead, ...rest];
}

// ---------------------------------------------------------------------------
// Queue lens — the single ranked task list (Sprint 26.0).
//
// The Queue is ONE ranked list of the agent's open tasks. Row #1 is the TOP
// task — the lowest `sort_order` — and that is its only meaning: it is
// highlighted because it is rank #1, NOT because it carries any runtime state
// and NOT a claim that anything is executing. Every agent with at least one
// open task therefore always has a highlighted #1. There is no `focus` status:
// the snapshot's `current` is simply rank #1, derived client-side.
//
// The list is assembled as `[current, ...up_next]` purely for ordering, then
// de-duplicated (the snapshot's `current` is also the head of `up_next`). The
// UI reads neither status nor runtime from it — only position. "Move to top" /
// drag reorders by `sort_order`.
// ---------------------------------------------------------------------------

export interface TaskQueueRow {
  task: Task;
  /** Row #1 — the top of the ranked list (highlighted: tint + position). Rank only. */
  isTop: boolean;
}

/**
 * The ranked task rows for the Queue lens. `current` (rank #1, the lowest
 * `sort_order` open task) leads, then the `sort_order`-ordered `up_next` with
 * `current` filtered out so it is never duplicated. With no current the list is
 * simply the queue, and its top row is still rank #1.
 */
export function taskQueueRows(current: BrainCurrent | Task | null, queue: Task[]): TaskQueueRow[] {
  const rest = current ? queue.filter((t) => t.id !== current.id) : queue;
  const ordered: Task[] = current ? [current, ...rest] : [...rest];
  return ordered.map((task, i) => ({ task, isTop: i === 0 }));
}

/** Total tasks in the ranked list — drives `Queue · N` and the toggle. */
export function taskQueueTotal(current: BrainCurrent | Task | null, queueTotal: number): number {
  // `current` is the head of up_next, so the counter already includes it.
  return current && queueTotal === 0 ? 1 : queueTotal;
}

/**
 * After a reorder, the id that now sits at rank #1 when it differs from the row
 * that was #1 before, else null. Drives whether a "move to top" is issued.
 */
export function topIdAfterReorder(orderedIds: string[], prevTopId: string | null): string | null {
  const top = orderedIds[0] ?? null;
  return top && top !== prevTopId ? top : null;
}

// ---------------------------------------------------------------------------
// Recently — consecutive-duplicate collapse (Phase C).
// ---------------------------------------------------------------------------

export interface CollapsedRecentEvent {
  /** The most-recent event of the collapsed run (drives the rendered prose + time). */
  event: RecentEvent;
  /** How many identical adjacent events this row stands for (≥ 1). */
  count: number;
}

/** Stable identity for "is this the same event" — ignores the timestamp + id. */
function recentKey(e: RecentEvent): string {
  return [e.task_id ?? "", e.from_state, e.to_state, e.to_is_terminal, e.actor_kind].join("|");
}

/**
 * Collapse runs of identical adjacent events into one row carrying a `count`.
 * Input is the locked 25.3 order (most-recent first); the kept event is the
 * most recent of each run, so its time reads correctly.
 */
export function collapseRecentDuplicates(events: RecentEvent[]): CollapsedRecentEvent[] {
  const out: CollapsedRecentEvent[] = [];
  for (const e of events) {
    const last = out[out.length - 1];
    if (last && recentKey(last.event) === recentKey(e)) {
      last.count += 1;
    } else {
      out.push({ event: e, count: 1 });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Ranked-row subtitle (Sprint 26.0) — real attributes only, no runtime words.
// ---------------------------------------------------------------------------

/**
 * The subtitle for a ranked task row: real structural attributes only — the
 * bound workflow's step position when one exists, otherwise `no workflow ·
 * adhoc`. NEVER a runtime claim ("running now", "working", a duration): nothing
 * executes asynchronously yet, so the row must not imply it. The richer
 * `currentWorkflow` (name + step/total) is used for whichever row is the
 * snapshot's current task; other rows fall back to the plain "in workflow"
 * marker, the task's workflow_name, or its document preview.
 */
export function rankedRowSubtitle(task: Task, currentWorkflow: TaskWorkflow | null): string {
  const guidance = task.body?.trim().split("\n")[0]?.trim() || "guiding task";
  if (currentWorkflow) {
    return `${currentWorkflow.name} · step ${currentWorkflow.step} of ${currentWorkflow.total}`;
  }
  if (task.workflow_name) return `${task.workflow_name} · in workflow`;
  if (task.workflow_instance_id) return `${guidance} · in workflow`;
  return `${guidance} · no workflow · adhoc`;
}
